import React, { useEffect, useRef, useState } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Button, Modal, Form, Input, message } from 'antd';
import { 
  MenuFoldOutlined, 
  MenuUnfoldOutlined,
  UserOutlined,
  LockOutlined,
  LogoutOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  TagsOutlined,
  HomeOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { authService } from '../services/authService';
import { authStorage } from '../utils/authStorage';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelect from './LanguageSelect';
import { EDISON_LOGO_URL } from '../constants/branding';

const { Header, Sider, Content } = AntLayout;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const loginUser = authStorage.get()?.loginUser;
  const manualWindowRef = useRef<Window | null>(null);
  const MANUAL_WINDOW_NAME = 'edison-admin-manual';

  const getManualUrl = () => `/manual/${language}/index.html`;

  const handleOpenManual = () => {
    const url = getManualUrl();
    const existing = manualWindowRef.current;
    if (existing && !existing.closed) {
      existing.location.href = url;
      existing.focus();
      return;
    }
    manualWindowRef.current = window.open(url, MANUAL_WINDOW_NAME, 'noopener,noreferrer');
  };

  useEffect(() => {
    const manualWin = manualWindowRef.current;
    if (!manualWin || manualWin.closed) {
      return;
    }
    manualWin.location.href = getManualUrl();
  }, [language]);

  // 菜单项配置
  const menuItems: MenuProps['items'] = [
    {
      key: '/homepage-config',
      icon: <HomeOutlined />,
      label: t('homepageConfig'),
    },
    {
      key: '/products',
      icon: <ShoppingOutlined />,
      label: t('productManagement'),
      children: [
        {
          key: '/products',
          label: t('productList'),
        },
      ],
    },
    {
      key: '/product-group',
      icon: <AppstoreOutlined />,
      label: t('productGroupManagement'),
    },
    {
      key: '/specs',
      icon: <TagsOutlined />,
      label: t('specManagement'),
    },
  ];

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'changePassword',
      icon: <LockOutlined />,
      label: t('changePassword'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('logout'),
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'changePassword') {
      setPasswordModalOpen(true);
    }
    if (key === 'logout') {
      authService.logout();
      navigate('/login', { replace: true });
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      setPasswordLoading(true);
      await authService.changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      message.success(t('changePasswordSuccess'));
      setPasswordModalOpen(false);
      passwordForm.resetFields();
      authService.logout();
      navigate('/login', { replace: true });
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || t('changePasswordFailed'));
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <AntLayout className="admin-layout">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        className="admin-sider"
        width={240}
      >
        <div className="admin-logo" data-i18n-ignore="true">
          <img
            src={EDISON_LOGO_URL}
            alt="Edison"
            className="admin-logo-image"
            title={t('appName')}
          />
          {!collapsed && <span className="admin-logo-text">{t('appName')}</span>}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          className="admin-menu"
        />
      </Sider>
      <AntLayout>
        <Header className="admin-header">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          <div style={{ flex: 1 }} />
          <Button
            type="text"
            icon={<BookOutlined />}
            onClick={handleOpenManual}
            data-i18n-ignore="true"
            style={{ marginRight: 12 }}
          >
            {t('operationManual')}
          </Button>
          <LanguageSelect style={{ width: 132, marginRight: 16 }} />
          <Dropdown
            menu={{ 
              items: userMenuItems,
              onClick: handleUserMenuClick
            }}
            placement="bottomRight"
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '0 12px'
            }}>
              <Avatar icon={<UserOutlined />} style={{ marginRight: '8px' }} />
              <span>{loginUser?.name || loginUser?.loginName || t('admin')}</span>
            </div>
          </Dropdown>
        </Header>
        <Content className="admin-content">
          {children}
        </Content>
      </AntLayout>
      <Modal
        title={t('changePassword')}
        open={passwordModalOpen}
        onOk={handleChangePassword}
        confirmLoading={passwordLoading}
        onCancel={() => {
          setPasswordModalOpen(false);
          passwordForm.resetFields();
        }}
        okText={t('confirm')}
        cancelText={t('cancel')}
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical" preserve={false}>
          <Form.Item
            label={t('oldPassword')}
            name="oldPassword"
            rules={[{ required: true, message: t('requiredOldPassword') }]}
          >
            <Input.Password placeholder={t('oldPasswordPlaceholder')} />
          </Form.Item>
          <Form.Item
            label={t('newPassword')}
            name="newPassword"
            rules={[{ required: true, message: t('requiredNewPassword') }]}
          >
            <Input.Password placeholder={t('newPasswordPlaceholder')} />
          </Form.Item>
          <Form.Item
            label={t('confirmPassword')}
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('requiredConfirmPassword') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('passwordMismatch')));
                },
              }),
            ]}
          >
            <Input.Password placeholder={t('confirmPasswordPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>
    </AntLayout>
  );
};

export default Layout;
